// src/context/AuthContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import api from "../api/axios";

export type Account = {
  id: string;
  name: string;
  domain: string | null;
  demo_mode: boolean;
};

export type User = {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
};

export type AuthContextType = {
  access: string | null;
  refresh: string | null;
  setTokens: (access: string, refresh: string) => void;
  logout: () => void;
  isLoading: boolean;
  accounts: Account[];
  currentAccountId: string | null;
  currentAccount: Account | null;
  setCurrentAccountId: (id: string) => void;
  user: User | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [access, setAccess] = useState<string | null>(null);
  const [refresh, setRefresh] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  // Load tokens + current account ID from localStorage once on mount
  useEffect(() => {
    const a = localStorage.getItem("access");
    const r = localStorage.getItem("refresh");
    const acctId = localStorage.getItem("current_account_id");

    if (a) setAccess(a);
    if (r) setRefresh(r);
    if (acctId) setCurrentAccountId(acctId); // ← optional, for hydration
    setIsLoading(false);
  }, []);

  // Whenever access token changes, configure axios and fetch /me
  useEffect(() => {
    if (access) {
      // Set the Authorization header
      api.defaults.headers.common["Authorization"] = `Bearer ${access}`;

      // Fetch user info & accounts
      (async () => {
        try {
          const { data } = await api.get("/users/me/");
          setAccounts(data.accounts);

          // Use stored account ID if valid, otherwise fall back to default
          const storedAccountId = localStorage.getItem("current_account_id");
          const accountIds = data.accounts.map((a: Account) => a.id);
          if (storedAccountId && accountIds.includes(storedAccountId)) {
            setCurrentAccountId(storedAccountId);
          } else {
            setCurrentAccountId(data.default_account_id);
          }

          // Store user info
          setUser({
            id: data.id,
            email: data.email,
            username: data.username,
            first_name: data.first_name,
            last_name: data.last_name,
            role: data.role || 'STANDARD',
          });

          // Identify user in PostHog
          if (typeof window !== 'undefined' && (window as any).posthog?.identify) {
            (window as any).posthog.identify(data.id, {
              email: data.email,
              name: `${data.first_name} ${data.last_name}`,
              role: data.role || 'STANDARD',
            });
          }
        } catch (err) {
          console.error("Failed to fetch /me, logging out", err);
          logout();
        }
      })();
    } else {
      // Clean up headers only - don't clear currentAccountId here
      // as it causes a race condition on page load that clears localStorage
      // before the stored account ID can be read. logout() handles full cleanup.
      delete api.defaults.headers.common["Authorization"];
      setAccounts([]);
      setUser(null);
    }
  }, [access]);

  // Whenever currentAccountId changes, update axios header and persist to localStorage
  useEffect(() => {
    if (currentAccountId) {
      api.defaults.headers.common["X-Account-ID"] = currentAccountId;
      localStorage.setItem("current_account_id", currentAccountId);
    } else {
      delete api.defaults.headers.common["X-Account-ID"];
      localStorage.removeItem("current_account_id");
    }
  }, [currentAccountId]);

  const setTokens = (a: string, r: string) => {
    localStorage.setItem("access", a);
    localStorage.setItem("refresh", r);
    setAccess(a);
    setRefresh(r);
  };

  const logout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.removeItem("current_account_id");  // ⬅️ don't forget this

    // Clear all scheme caches from sessionStorage
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('eliotnest_')) {
        sessionStorage.removeItem(key);
      }
    });

    // Reset PostHog identity on logout
    if (typeof window !== 'undefined' && (window as any).posthog?.reset) {
      (window as any).posthog.reset();
    }

    setAccess(null);
    setRefresh(null);
    setAccounts([]);
    setCurrentAccountId(null);

    // ✅ Clear axios global headers
    delete api.defaults.headers.common["Authorization"];
    delete api.defaults.headers.common["X-Account-ID"];
  };

  // Compute current account from accounts and currentAccountId
  const currentAccount = accounts.find((a) => a.id === currentAccountId) || null;

  return (
    <AuthContext.Provider
      value={{
        access,
        refresh,
        setTokens,
        logout,
        isLoading,
        user,
        accounts,
        currentAccountId,
        currentAccount,
        setCurrentAccountId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
};
